import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { BranchProvider, useBranch } from "@/contexts/BranchContext";
import { BrandLogo } from "@/components/BrandLogo";
import BranchSelector from "@/components/BranchSelector";
import { Button } from "@/components/ui/button";
import { LogOut, LayoutDashboard, FileText, Calendar, FolderOpen, Map, Truck, Ticket, Menu, X, PanelLeftClose, PanelLeftOpen, HelpCircle, Users } from "lucide-react";
import SilviaButton from "@/components/SilviaButton";
import { WelcomeVideoModal } from "@/components/WelcomeVideoModal";
import { PortalProfileMenu } from "@/components/PortalProfileMenu";
import api from "@/lib/api";

function PortalLayout() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const { hasBranches, selectedBranch } = useBranch();
    const handleLogout = () => { logout(); nav("/login"); };
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [videoOpen, setVideoOpen] = useState(false);
    const [welcomeSeen, setWelcomeSeen] = useState(true);

    useEffect(() => {
        let active = true;
        api.get("/portal/welcome-status")
            .then(({ data }) => {
                if (!active) return;
                if (!data?.seen) { setWelcomeSeen(false); setVideoOpen(true); }
            })
            .catch(() => {});
        return () => { active = false; };
    }, []);

    const markSeen = () => {
        if (!welcomeSeen) {
            setWelcomeSeen(true);
            api.post("/portal/welcome-seen").catch(() => {});
        }
    };

    const handleVideoOpenChange = (v) => {
        setVideoOpen(v);
        if (!v) markSeen();
    };

    const links = [
        { to: "/portal", icon: LayoutDashboard, label: "Dashboard", slug: "dashboard", end: true },
        { to: "/portal/mapas", icon: Map, label: "Mapas", slug: "mapas" },
        { to: "/portal/transportes", icon: Truck, label: "Transportes", slug: "transportes" },
        { to: "/portal/reports", icon: FileText, label: "Reportes", slug: "reportes" },
        { to: "/portal/calendar", icon: Calendar, label: "Calendario", slug: "calendario" },
        { to: "/portal/documents", icon: FolderOpen, label: "Documentos", slug: "documentos" },
        { to: "/portal/soporte", icon: Ticket, label: "Soporte", slug: "soporte" },
        ...(user?.is_client_admin ? [{ to: "/portal/usuarios", icon: Users, label: "Usuarios", slug: "usuarios" }] : []),
    ];

    return (
        <div className="min-h-screen bg-background flex">
            {/* Sidebar — desktop */}
            <aside
                className={`hidden lg:flex flex-col bg-[hsl(var(--trulyn-navy))] text-white shrink-0 transition-all duration-300 ${
                    collapsed ? "w-[68px]" : "w-64"
                }`}
                data-testid="portal-sidebar"
            >
                <div className={`px-2 py-4 border-b border-white/10 flex items-center ${collapsed ? "justify-center" : "justify-between pl-4"}`}>
                    {!collapsed && <BrandLogo size={65} light />}
                    <button
                        onClick={() => setCollapsed(c => !c)}
                        className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                        title={collapsed ? "Expandir menú" : "Colapsar menú"}
                        data-testid="portal-sidebar-toggle"
                    >
                        {collapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
                    </button>
                </div>
                <nav className="flex-1 px-1.5 py-3 space-y-0.5">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.end}
                            className={({ isActive }) =>
                                `flex items-center gap-2.5 rounded-lg text-sm font-medium transition-colors ${
                                    collapsed ? "justify-center px-2 py-2.5" : "px-3 py-2"
                                } ${
                                    isActive
                                        ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]"
                                        : "text-white/85 hover:bg-white/10"
                                }`
                            }
                            title={collapsed ? l.label : undefined}
                            data-testid={`portal-nav-${l.slug}`}
                        >
                            <l.icon className={collapsed ? "w-5 h-5" : "w-4 h-4"} />
                            {!collapsed && <span className="flex-1">{l.label}</span>}
                        </NavLink>
                    ))}
                </nav>
                <div className={`p-2 border-t border-white/10 ${collapsed ? "text-center" : "px-3"}`}>
                    {!collapsed && (
                        <>
                            <div className="text-xs text-white/80 font-medium mb-0.5">{user?.full_name}</div>
                            <div className="text-[10px] text-white/50 mb-2">
                                {hasBranches && selectedBranch ? selectedBranch.name : "Cliente"}
                            </div>
                        </>
                    )}
                    {collapsed ? (
                        <button
                            onClick={handleLogout}
                            className="p-2 rounded-lg hover:bg-white/10 text-white/70 hover:text-white transition-colors"
                            title="Salir"
                            data-testid="portal-logout-button"
                        >
                            <LogOut className="w-5 h-5" />
                        </button>
                    ) : (
                        <Button variant="outline" size="sm" onClick={handleLogout} className="w-full bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white" data-testid="portal-logout-button">
                            <LogOut className="w-4 h-4 mr-1" /> Salir
                        </Button>
                    )}
                </div>
            </aside>

            {/* Mobile drawer overlay */}
            {drawerOpen && (
                <div className="fixed inset-0 z-50 lg:hidden" data-testid="portal-drawer-overlay">
                    <div className="absolute inset-0 bg-black/50" onClick={() => setDrawerOpen(false)} />
                    <aside className="absolute left-0 top-0 bottom-0 w-72 bg-[hsl(var(--trulyn-navy))] text-white flex flex-col animate-in slide-in-from-left duration-200">
                        <div className="px-4 py-4 border-b border-white/10 flex items-center justify-between">
                            <BrandLogo size={40} light withSubtitle={false} />
                            <button onClick={() => setDrawerOpen(false)} className="p-1.5 rounded-lg hover:bg-white/10" data-testid="portal-drawer-close">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <nav className="flex-1 px-2 py-3 space-y-0.5">
                            {links.map((l) => (
                                <NavLink
                                    key={l.to}
                                    to={l.to}
                                    end={l.end}
                                    onClick={() => setDrawerOpen(false)}
                                    className={({ isActive }) =>
                                        `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                            isActive
                                                ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]"
                                                : "text-white/85 hover:bg-white/10"
                                        }`
                                    }
                                    data-testid={`portal-drawer-${l.slug}`}
                                >
                                    <l.icon className="w-5 h-5" />
                                    <span className="flex-1">{l.label}</span>
                                </NavLink>
                            ))}
                        </nav>
                        <div className="p-3 border-t border-white/10">
                            <div className="text-sm text-white/80 font-medium mb-0.5">{user?.full_name}</div>
                            <div className="text-xs text-white/50 mb-3">
                                {hasBranches && selectedBranch ? selectedBranch.name : "Cliente"}
                            </div>
                            <Button variant="outline" size="sm" onClick={handleLogout} className="w-full bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white">
                                <LogOut className="w-4 h-4 mr-1" /> Salir
                            </Button>
                        </div>
                    </aside>
                </div>
            )}

            {/* Main content */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Top bar */}
                <header className="sticky top-0 z-30 bg-white border-b">
                    <div className="px-4 lg:px-6 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-3 lg:hidden">
                            <button onClick={() => setDrawerOpen(true)} className="p-1.5 -ml-1 rounded-lg hover:bg-secondary" data-testid="portal-menu-btn">
                                <Menu className="w-6 h-6" />
                            </button>
                            <NavLink to="/portal" data-testid="portal-home-link">
                                <BrandLogo size={28} />
                            </NavLink>
                        </div>
                        <div className="hidden lg:flex items-center gap-2 text-sm">
                            <span className="text-muted-foreground">Portal del Cliente</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => setVideoOpen(true)}
                                className="flex items-center justify-center w-9 h-9 rounded-full bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 transition-colors"
                                title="Ver video de ayuda"
                                aria-label="Ver video de ayuda"
                                data-testid="portal-help-btn"
                            >
                                <HelpCircle className="w-5 h-5" />
                            </button>
                            <div className="text-sm text-right">
                                <PortalProfileMenu />
                            </div>
                        </div>
                    </div>
                </header>

                <WelcomeVideoModal open={videoOpen} onOpenChange={handleVideoOpenChange} />

                {/* Branch selector — only for multi-branch clients */}
                {hasBranches && <BranchSelector />}

                <main className="flex-1 p-4 lg:p-6 overflow-auto">
                    <Outlet />
                </main>
            </div>
            <SilviaButton sidebarCollapsed={collapsed} />
        </div>
    );
}

export default function PortalShell() {
    return (
        <BranchProvider>
            <PortalLayout />
        </BranchProvider>
    );
}
